import { useMutation } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { LayoutTemplate, ArrowRightLeft } from 'lucide-react';
import { round } from '@/utils/mathUtils';
import { useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';

import { StyledCellColumn } from '@/components/custom-ui/TableColumns/StyledCell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useItemStore } from '@/store';
import { DataTable } from '@/UI/TableShadcn';
import { formatNumberWithCommas } from '@/utils/FormatNumber';
import { cn } from 'lib/utils';

import { PostOneRequestTransfer } from '../api/saleToIncome.api';
import type {
  IInvoiceSaleFullCanEdit,
  IStoreOutToCustomerBodyWithEdit,
} from '../api/saleToIncome.interface';

import ResponseResultDialog from './ResponseResultDialog';

type Props = {
  Items: IStoreOutToCustomerBodyWithEdit[] | undefined;
  clientID: number;
  salesID: number;
  setLoadingConvert: (value: React.SetStateAction<boolean>) => void;
  setInvoiceData: (
    value: React.SetStateAction<IInvoiceSaleFullCanEdit | null>,
  ) => void;
};

function ItemsTable({
  Items,
  clientID,
  salesID,
  setLoadingConvert,
  setInvoiceData,
}: Props) {
  const { openItemDialog } = useItemStore();
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [requestID, setRequestID] = useState<string | null>(null);

  const [editedItems, setEditedItems] = useState<
    IStoreOutToCustomerBodyWithEdit[]
  >(Items || []);

  const handleEditDiscountChange = (id: number, newValue: string) => {
    setEditedItems((prev) =>
      prev.map((item) =>
        item.ID === id
          ? { ...item, EditDiscount: Number(newValue) || 0 }
          : item,
      ),
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab') {
        // Prevent default tab behavior
        e.preventDefault();

        // Get all input fields with the discount-input class
        const inputs = Array.from(
          document.querySelectorAll('.discount-input'),
        ) as HTMLInputElement[];

        // Log all inputs for debugging
        inputs.forEach((_input, _idx) => {
        });

        // Find the current input index in our collection
        const currentInputIndex = inputs.findIndex(
          (input) => input === e.target,
        );

        if (currentInputIndex !== -1) {
          // Calculate next input index (for Tab or Shift+Tab)
          const nextIndex = e.shiftKey
            ? (currentInputIndex - 1 + inputs.length) % inputs.length
            : (currentInputIndex + 1) % inputs.length;

          // Focus and select the input immediately
          inputs[nextIndex]?.focus();
          inputs[nextIndex]?.select();
        }
      }
    },
    [],
  );

  const addRequestMutation = useMutation({
    mutationFn: PostOneRequestTransfer,
    onSuccess: async (data) => {
      setRequestID(data?.healthyRequestID);
      setFinishDialogOpen(true);
      setLoadingConvert(false);
      toast.success('تم تحويل فاتورة البيع الي  طلبية وارد  بنجاح');
    },

    onError: (err: AxiosError<{ message?: string }>) => {
      setLoadingConvert(false);
      toast.error(err?.response?.data?.message || 'حدث خطأ ما');
    },
  });

  const handleCloseDialog = () => {
    setFinishDialogOpen(false);
    setInvoiceData(null);
  };
  const columns = useMemo<ColumnDef<IStoreOutToCustomerBodyWithEdit>[]>(
    () => [
      {
        accessorKey: 'ID',
        header: '#',
        cell: (row) => {
          return (
            <div className="flex w-full items-center justify-center font-roboto text-sm font-normal">
              {row.row.index + 1}
            </div>
          );
        },
      },
      {
        accessorKey: 'Name',
        header: 'الصنف',
        cell: (row) => {
          return (
            <div className="flex h-10 w-full items-center justify-start">
              <Button
                variant="link"
                size="default"
                className="line-clamp-1 font-sans text-lg"
                onClick={() => {
                  openItemDialog(row.row.original.Item_ID);
                }}
              >
                {row.row.original.Name}
              </Button>
            </div>
          );
        },
      },
      {
        accessorKey: 'Price',
        header: 'الجمهور',
        cell: (row) => {
          return (
            <div className="flex items-center justify-center font-roboto text-xl">
              {round(row?.row?.original?.Price || 0, 2) || '-'}
            </div>
          );
        },
      },
      {
        accessorKey: 'Qty',
        header: 'الكمية',
        cell: (row) => {
          return (
            <div className="flex items-center justify-center font-roboto text-xl">
              {round(row?.row?.original?.Qty || 0, 0) || '-'}
            </div>
          );
        },
      },

      {
        accessorKey: 'Discount',
        header: ' خصم',
        cell: (row) => {
          return (
            <StyledCellColumn
              value={formatNumberWithCommas(row?.row?.original?.Discount)}
            />
          );
        },
      },
      {
        accessorKey: 'EditDiscount',
        header: 'الخصم المعدل',
        cell: (row) => {
          const rowIndex = row.row.index;
          return (
            <Input
              type="number"
              className={cn(
                'discount-input h-8 w-32 text-center text-xl',
                round(row.row.original.Discount, 2) !==
                  round(row.row.original.EditDiscount, 2) &&
                  'bg-gradient-to-r from-indigo-500 to-purple-500 text-white',
              )}
              defaultValue={
                round(
                  editedItems.find((item) => item.ID === row.row.original.ID)
                    ?.EditDiscount || 0,
                  2,
                ) || 0
              }
              id={`edit-discount-${row.row.original.ID}`}
              tabIndex={rowIndex * 2 + 1} // Sequential tabbing
              onBlur={(e) =>
                handleEditDiscountChange(row.row.original.ID, e.target.value)
              }
              onKeyDown={(e) => handleKeyDown(e)}
            />
          );
        },
      },

      {
        accessorKey: 'TrDate',
        header: 'الباتش',
        cell: (row) => {
          return (
            <div className="flex flex-col items-center justify-center gap-0.5 dark:contrast-75">
              <h3 className="line-clamp-1 w-20 rounded bg-green-100 text-center font-sans text-xs text-green-800">
                {row?.row?.original?.BNO}
              </h3>
              <h3 className="line-clamp-1 w-20 rounded bg-red-100 text-center font-sans text-xs text-red-700">
                {dayjs(new Date(row.row.original.ExpireDate)).format(
                  'DD/MM/YYYY',
                )}
              </h3>
            </div>
          );
        },
      },
    ],
    [openItemDialog, editedItems, handleKeyDown],
    // end
  );

  return (
    <div className="mt-1 p-0.5">
      {Items && Items.length > 0 ? (
        <div>
          <div className="relative overflow-hidden rounded-md bg-gradient-to-r from-indigo-50/50 to-purple-50/50 p-3 dark:from-indigo-950/20 dark:to-purple-950/20">
            <div className="absolute left-2 top-2 h-6 w-6 rounded-br-xl border-b border-r border-indigo-200/30 dark:border-indigo-700/30"></div>
            <div className="absolute bottom-2 right-2 h-6 w-6 rounded-tl-xl border-l border-t border-indigo-200/30 dark:border-indigo-700/30"></div>

            <DataTable
              columns={columns}
              data={editedItems}
              visibleColumns={[]}
              isPaginated={false}
              isTopToolbar={false}
              heightTable="63vh"
            />
          </div>

          {/* Discount Summary Section */}
          <div className="mt-3 overflow-hidden rounded-lg border border-indigo-200/30 bg-gradient-to-tr from-indigo-50/50 via-purple-50/50 to-indigo-50/50 p-2.5 shadow-sm dark:border-indigo-700/30 dark:from-indigo-950/20 dark:via-purple-950/20 dark:to-indigo-950/20">
            <div className="flex flex-col space-y-2">
              <h4 className="flex items-center gap-2 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                <span className="inline-block h-1 w-4 rounded-full bg-indigo-600 dark:bg-indigo-400"></span>
                ملخص التعديلات
              </h4>

              <div className="grid grid-cols-3 gap-2">
                {/* Original Total */}
                <div className="flex flex-col rounded-lg bg-white/80 p-2 shadow-sm dark:bg-gray-800/80">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                    المجموع (خصم أصلي)
                  </span>
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                    {formatNumberWithCommas(
                      round(
                        editedItems.reduce(
                          (sum, item) =>
                            sum +
                            item.Price * item.Qty * (1 - item.Discount / 100),
                          0,
                        ),
                        2,
                      ),
                    )}
                  </span>
                </div>

                {/* New Total */}
                <div className="flex flex-col rounded-lg bg-white/80 p-2 shadow-sm dark:bg-gray-800/80">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                    المجموع (خصم معدل)
                  </span>
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                    {formatNumberWithCommas(
                      round(
                        editedItems.reduce(
                          (sum, item) =>
                            sum +
                            item.Price *
                              item.Qty *
                              (1 - item.EditDiscount / 100),
                          0,
                        ),
                        2,
                      ),
                    )}
                  </span>
                </div>

                {/* Difference */}
                {(() => {
                  const originalTotal = editedItems.reduce(
                    (sum, item) =>
                      sum + item.Price * item.Qty * (1 - item.Discount / 100),
                    0,
                  );
                  const newTotal = editedItems.reduce(
                    (sum, item) =>
                      sum +
                      item.Price * item.Qty * (1 - item.EditDiscount / 100),
                    0,
                  );
                  const difference = round(newTotal - originalTotal, 2);
                  const percentChange = originalTotal
                    ? round((difference / originalTotal) * 100, 2)
                    : 0;

                  return (
                    <div className="flex flex-col rounded-lg bg-gradient-to-r from-indigo-100/80 to-purple-100/80 p-2 shadow-sm dark:from-indigo-900/40 dark:to-purple-900/40">
                      <span className="text-[10px] font-medium text-indigo-700 dark:text-indigo-300">
                        الفرق ({percentChange > 0 ? '+' : ''}
                        {percentChange}%)
                      </span>
                      <span
                        className={`text-xs font-bold ${
                          difference > 0
                            ? 'text-green-600 dark:text-green-400'
                            : difference < 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {difference > 0 ? '+' : ''}
                        {formatNumberWithCommas(difference)}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              className={cn(
                'h-8 rounded-md px-4 text-xs font-medium',
                !editedItems.some((item) => item.Discount !== item.EditDiscount)
                  ? 'bg-gray-100 text-gray-400 dark:bg-gray-800'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:from-indigo-700 hover:to-purple-700',
              )}
              disabled={
                !editedItems.some((item) => item.Discount !== item.EditDiscount)
              }
              onClick={async () => {
                setLoadingConvert(true);
                await addRequestMutation.mutateAsync({
                  clientID: clientID,
                  salesID: salesID,
                  items: editedItems.map((item) => ({
                    ID: item.Item_ID,
                    Price: item.Price,
                    TotalAfterDiscount:
                      item.Price * item.Qty * (1 - item.EditDiscount / 100),
                    Qty: item.Qty,
                    Discount: item.EditDiscount,
                    ExpireDate: item.ExpireDate,
                    BNO: item.BNO,
                  })),
                });
              }}
            >
              <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" />
              تحويل الي طلبية وارد
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex h-[70vh] flex-col items-center justify-center rounded-md bg-gradient-to-r from-indigo-50/50 to-purple-50/50 p-3 dark:from-indigo-950/20 dark:to-purple-950/20">
          <div className="flex flex-col items-center justify-center">
            <LayoutTemplate className="h-24 w-24 text-indigo-300 dark:text-indigo-700" />
            <h3 className="mt-2 text-sm font-medium text-indigo-600 dark:text-indigo-400">
              لا يوجد اصناف
            </h3>
          </div>
        </div>
      )}

      <ResponseResultDialog
        closeFn={handleCloseDialog}
        isOpen={finishDialogOpen}
        requestID={requestID}
      />
    </div>
  );
}

export default ItemsTable;
